/**
 * LayerPicker — Stage I.
 *
 * Two things in one small panel:
 *   1. Active layer radio — where new elements are drawn. Drives
 *      `toolOptions.activeLayer`. Players can't pick "dm".
 *   2. Per-layer visibility toggles — local-only filter for what gets
 *      rendered on this viewer's canvas. DMs use this to hide the
 *      player-scratch layer during a boss fight without touching any
 *      shared state. The server has already stripped layers the viewer
 *      can't see (DM layer from players), so for players the DM toggle
 *      is just hidden.
 *
 * Lives top-right of the VTT page beside the connection badge. Uses
 * `useSyncExternalStore` so only this component rerenders when layer
 * state changes — the canvas picks up the change via its own store
 * subscription.
 */

import { useSyncExternalStore } from "react";
import { sceneStore } from "../store/sceneStore";
import type { SceneLayer } from "../../lib/scene";
import "./LayerPicker.css";

interface LayerDef {
  key: SceneLayer;
  label: string;
  /** Short hint — rendered in the button's title tooltip. */
  hint: string;
  /** Accent colour for the visibility dot. */
  accent: string;
}

const LAYERS: LayerDef[] = [
  { key: "dm",     label: "DM",     hint: "DM-only. Never sent to players.",         accent: "#f87171" },
  { key: "shared", label: "Shared", hint: "Everyone sees and can draw here.",       accent: "#f5c77a" },
  { key: "player", label: "Player", hint: "Per-player scratch. DMs see all.",       accent: "#6ac1c4" },
];

export default function LayerPicker() {
  // Subscribe via the per-notify counter — getState() returns a stable
  // reference, so React would otherwise short-circuit re-renders and
  // leave this picker permanently stale on store mutations.
  useSyncExternalStore(
    (cb) => sceneStore.subscribe(cb),
    () => sceneStore.getNotifyVersion(),
    () => sceneStore.getNotifyVersion(),
  );
  const state = sceneStore.getState();

  const visibleLayers = state.visibleLayers;
  const activeLayer = state.toolOptions.activeLayer;
  const isDm = state.isDm;

  // Players should see only shared + their own player layer as options.
  const layers = LAYERS.filter((l) => isDm || l.key !== "dm");

  return (
    <div className="layer-picker" aria-label="Layer controls">
      <div className="layer-picker__heading">Layers</div>
      {layers.map((l) => {
        const isActive = activeLayer === l.key;
        const isVisible = visibleLayers.has(l.key);
        return (
          <div key={l.key} className="layer-picker__row">
            <button
              type="button"
              className={
                "layer-picker__active" +
                (isActive ? " layer-picker__active--on" : "")
              }
              onClick={() => sceneStore.setToolOptions({ activeLayer: l.key })}
              title={`Draw on ${l.label} — ${l.hint}`}
            >
              <span
                className="layer-picker__dot"
                style={{ background: l.accent }}
              />
              <span className="layer-picker__label">{l.label}</span>
            </button>
            <button
              type="button"
              className={
                "layer-picker__eye" +
                (isVisible ? "" : " layer-picker__eye--off")
              }
              onClick={() => sceneStore.setLayerVisible(l.key, !isVisible)}
              title={isVisible ? `Hide ${l.label}` : `Show ${l.label}`}
              aria-pressed={isVisible}
            >
              {isVisible ? "◉" : "○"}
            </button>
          </div>
        );
      })}
    </div>
  );
}
